import React, { useState, useRef } from 'react'
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Switch,
  FormControlLabel,
  useMediaQuery,
  useTheme
} from '@mui/material'
import {
  Send as SendIcon,
  Spellcheck as GrammarIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Lightbulb as TipIcon,
  Speed as StreamIcon
} from '@mui/icons-material'
import { motion } from 'framer-motion'
import { apiClient, API_ENDPOINTS, buildApiUrl } from '../config/api'
import toast from 'react-hot-toast'
import ReactMarkdown from 'react-markdown'


const grammarExamples = [
  {
    text: "I am go to school everyday.",
    level: "A1",
    topic: "Present Simple"
  },
  {
    text: "She have been working here since 2020.",
    level: "B1",
    topic: "Present Perfect"
  },
  {
    text: "If I would have more time, I will study English.",
    level: "B2",
    topic: "Conditional Sentences"
  },
  {
    text: "The book which I bought it yesterday is very interesting.",
    level: "B2",
    topic: "Relative Clauses"
  }
]

function Grammar() {
  const [inputText, setInputText] = useState('')
  const [studentLevel, setStudentLevel] = useState('B1')
  const [isLoading, setIsLoading] = useState(false)
  const [feedback, setFeedback] = useState(null)
  const [history, setHistory] = useState([])
  const [streamingEnabled, setStreamingEnabled] = useState(true)
  const [isStreaming, setIsStreaming] = useState(false)
  const [streamingFeedback, setStreamingFeedback] = useState('')
  const eventSourceRef = useRef(null)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handleSubmit = async () => {
    if (!inputText.trim()) {
      toast.error('Vui lòng nhập câu tiếng Anh cần kiểm tra')
      return
    }

    if (streamingEnabled) {
      await handleStreamingGrammar()
    } else {
      await handleRegularGrammar()
    }
  }

  const handleRegularGrammar = async () => {
    setIsLoading(true)
    try {
      const response = await apiClient.post(API_ENDPOINTS.TEACHER.GRAMMAR, {
        text: inputText,
        studentLevel
      })
      
      setFeedback(response.data)
      setHistory(prev => [response.data, ...prev.slice(0, 4)]) // Keep last 5 items
      toast.success('Đã phân tích ngữ pháp!')
    } catch (error) {
      console.error('Grammar check error:', error)
      toast.error('Có lỗi xảy ra khi kiểm tra ngữ pháp')
    } finally {
      setIsLoading(false)
    }
  }

  const handleStreamingGrammar = async () => {
    setIsStreaming(true)
    setStreamingFeedback('')
    setFeedback(null)
    
    try {
      const response = await fetch(buildApiUrl(API_ENDPOINTS.TEACHER.GRAMMAR_STREAM), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: inputText,
          studentLevel
        })
      })

      if (!response.ok) {
        throw new Error('Network response was not ok')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let fullContent = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6)
            
            if (data === '[DONE]') continue
            
            try {
              const parsed = JSON.parse(data)
              
              if (parsed.type === 'chunk' && parsed.content) {
                fullContent += parsed.content
                setStreamingFeedback(fullContent)
              } else if (parsed.type === 'complete') {
                const completeFeedback = {
                  original: inputText,
                  feedback: parsed.fullContent || fullContent,
                  timestamp: new Date().toISOString()
                }
                setFeedback(completeFeedback)
                setHistory(prev => [completeFeedback, ...prev.slice(0, 4)])
                toast.success('Đã phân tích ngữ pháp!')
              } else if (parsed.type === 'error') {
                throw new Error(parsed.error)
              }
            } catch (parseError) {
              console.error('Parse error:', parseError)
            }
          }
        }
      }
    } catch (error) {
      console.error('Streaming grammar error:', error)
      toast.error('Có lỗi xảy ra khi kiểm tra ngữ pháp')
    } finally {
      setIsStreaming(false)
      setStreamingFeedback('')
    }
  }

  const handleExampleClick = (example) => {
    setInputText(example.text)
    setStudentLevel(example.level)
  }

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && event.ctrlKey) {
      handleSubmit()
    }
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <GrammarIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h3" component="h1" sx={{ mb: 2, fontWeight: 600 }}>
            Kiểm tra ngữ pháp
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            AI sẽ phân tích và sửa lỗi ngữ pháp, giải thích chi tiết cách sửa và đưa ra gợi ý luyện tập
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Input Section */}
          <Grid item xs={12} md={8}>
            <Paper elevation={3} sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                📝 Nhập câu cần kiểm tra
              </Typography>
              
              {/* Level Selection */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Trình độ của bạn:</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {['A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map((level) => (
                    <Chip
                      key={level}
                      label={level}
                      onClick={() => setStudentLevel(level)}
                      color={studentLevel === level ? 'primary' : 'default'}
                      variant={studentLevel === level ? 'filled' : 'outlined'}
                      sx={{ cursor: 'pointer' }}
                      size={isMobile ? 'small' : 'medium'}
                    />
                  ))}
                </Box>
              </Box>

              {/* Streaming Toggle */}
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={streamingEnabled}
                      onChange={(e) => setStreamingEnabled(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <StreamIcon fontSize="small" />
                      <Typography variant="body2">
                        Streaming Mode {streamingEnabled ? '(Bật)' : '(Tắt)'}
                      </Typography>
                    </Box>
                  }
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {streamingEnabled 
                    ? 'Phản hồi sẽ hiển thị từng phần khi AI đang phân tích' 
                    : 'Chờ AI hoàn thành phân tích trước khi hiển thị kết quả'
                  }
                </Typography>
              </Box>

              <TextField
                fullWidth
                multiline
                rows={6}
                placeholder="Nhập câu tiếng Anh cần kiểm tra ngữ pháp...\n\nVí dụ: I am go to school everyday."
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                sx={{ mb: 3 }}
              />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Nhấn Ctrl + Enter để kiểm tra nhanh
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleSubmit}
                  disabled={isLoading || isStreaming}
                  startIcon={(isLoading || isStreaming) ? <CircularProgress size={20} /> : <SendIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                    px: 4
                  }}
                >
                  {isStreaming ? 'Ligo đang phân tích...' : isLoading ? 'Đang kiểm tra...' : 'Kiểm tra ngữ pháp'}
                </Button>
              </Box>
            </Paper>

            {/* Streaming Feedback Section */}
            {isStreaming && streamingFeedback && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Đang phân tích...
                  </Typography>
                  
                  <Box sx={{ 
                    p: 3, 
                    backgroundColor: 'grey.50', 
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'grey.200'
                  }}>
                    <ReactMarkdown>{streamingFeedback}</ReactMarkdown>
                    <Box component="span" sx={{
                      display: 'inline-block',
                      width: '2px',
                      height: '1.2em',
                      backgroundColor: 'primary.main',
                      marginLeft: '2px',
                      animation: 'blink 1s infinite'
                    }} />
                  </Box>
                </Paper>
              </motion.div>
            )}

            {/* Feedback Section */}
            {feedback && !isStreaming && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                    <CheckIcon sx={{ mr: 1, color: 'success.main' }} />
                    Kết quả phân tích
                  </Typography>
                  
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Câu gốc:</Typography>
                    <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                      "{feedback.original}"
                    </Typography>
                  </Alert>
                  
                  <Card variant="outlined" sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                      📚 Phản hồi từ AI Teacher:
                    </Typography>
                    <Box sx={{ '& p': { mb: 2 }, '& ul': { pl: 3 }, '& li': { mb: 1 } }}>
                      <ReactMarkdown>{feedback.feedback}</ReactMarkdown>
                    </Box>
                  </Card>
                  
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                    Phân tích lúc: {new Date(feedback.timestamp).toLocaleString('vi-VN')}
                  </Typography>
                </Paper>
              </motion.div>
            )}
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            {/* Examples */}
            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <TipIcon sx={{ mr: 1, color: 'warning.main' }} />
                Ví dụ mẫu
              </Typography>
              
              {grammarExamples.map((example, index) => (
                <Card
                  key={index}
                  variant="outlined"
                  sx={{
                    mb: 2,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      transform: 'translateY(-2px)'
                    }
                  }}
                  onClick={() => handleExampleClick(example)}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Chip label={example.level} size="small" color="primary" />
                      <Chip label={example.topic} size="small" variant="outlined" />
                    </Box>
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                      "{example.text}"
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Paper>

            {/* History */}
            {history.length > 0 && (
              <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  📋 Lịch sử kiểm tra
                </Typography>
                
                {history.map((item, index) => (
                  <Box key={index}>
                    <Card variant="outlined" sx={{ mb: 2, p: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                        "{item.original&&item.original.substring(0, 50)}{item.original&&item.original.length > 50 ? '...' : ''}"
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(item.timestamp).toLocaleString('vi-VN')}
                      </Typography>
                    </Card>
                    {index < history.length - 1 && <Divider sx={{ my: 1 }} />}
                  </Box>
                ))}
              </Paper>
            )}
          </Grid>
        </Grid>
      </motion.div>
    </Container>
  )
}

export default Grammar